
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Plus, Clock } from 'lucide-react';
import { UnitSystem } from '@/utils/unitConversion/types';
import { ShoppingListResponse, CustomShoppingItem } from '@/types/shoppingList';
import ShoppingListHeader from './ShoppingListHeader';
import ShoppingListByCategory from './ShoppingListByCategory';
import RecipeIngredientsTab from './RecipeIngredientsTab';
import AddCustomItemForm from './AddCustomItemForm';
import ReactivateItemsSheet from './ReactivateItemsSheet';

interface ShoppingListDisplayProps {
  shoppingList: ShoppingListResponse | null;
  preferredUnits: UnitSystem;
  adults: number;
  kids: number;
  shoppingMode?: boolean;
  selectedDates?: string[];
  mealPlansMap?: Record<string, any[]>;
  isLoadingShoppingList?: boolean;
  processingStage?: string;
  progress?: number;
  customItems?: Record<string, CustomShoppingItem[]>;
  inactiveItems?: CustomShoppingItem[];
  onItemCheckedChange: (category: string, itemId: string, checked: boolean) => void;
  onCustomItemCheckedChange?: (itemId: string, checked: boolean) => void;
  onAddCustomItem?: (name: string, category: string, quantity?: string, note?: string) => void;
  onRemoveCustomItem?: (itemId: string) => void;
  onReactivateItem?: (itemId: string) => void;
  onShoppingModeToggle?: () => void;
  onEditSettings?: () => void;
}

const ShoppingListDisplay: React.FC<ShoppingListDisplayProps> = ({
  shoppingList,
  preferredUnits,
  adults,
  kids,
  shoppingMode = false,
  selectedDates,
  mealPlansMap,
  isLoadingShoppingList = false,
  processingStage = '',
  progress = 0,
  customItems = {},
  inactiveItems = [],
  onItemCheckedChange,
  onCustomItemCheckedChange,
  onAddCustomItem,
  onRemoveCustomItem,
  onReactivateItem,
  onShoppingModeToggle,
  onEditSettings,
}) => {
  const [showAddItemSheet, setShowAddItemSheet] = useState(false);
  const [showReactivateSheet, setShowReactivateSheet] = useState(false);

  // Calculate meal type counts based on selected dates
  const getMealTypeCounts = () => {
    // Use persisted meal type counts from shopping list if available
    if (shoppingList?.generationParams?.mealTypeCounts) {
      return shoppingList.generationParams.mealTypeCounts;
    }

    // Fallback to current form state calculation
    if (!selectedDates || !mealPlansMap) {
      return { breakfast: 0, lunch: 0, dinner: 0 };
    }

    const counts = { breakfast: 0, lunch: 0, dinner: 0 };
    
    selectedDates.forEach(date => {
      const mealsForDate = mealPlansMap[date] || [];
      mealsForDate.forEach(meal => {
        if (meal.mealType && counts.hasOwnProperty(meal.mealType)) {
          counts[meal.mealType as keyof typeof counts]++;
        }
      });
    });

    return counts;
  };

  // Calculate total ingredient count (including custom items)
  const getTotalIngredientCount = () => {
    if (!shoppingList?.categories) return 0;
    const recipeItemCount = Object.values(shoppingList.categories).reduce((total, items) => total + items.length, 0);
    const customItemCount = Object.values(customItems).reduce((total, items) => total + items.length, 0);
    return recipeItemCount + customItemCount;
  };

  // Calculate actual recipe count - use the recipes from the shopping list
  const getActualRecipeCount = () => {
    return shoppingList?.recipes?.length || 0;
  };

  // Get all existing categories for the add form
  const getExistingCategories = () => {
    const categories = new Set<string>();
    if (shoppingList?.categories) {
      Object.keys(shoppingList.categories).forEach(cat => categories.add(cat));
    }
    Object.keys(customItems).forEach(cat => categories.add(cat));
    return Array.from(categories);
  };

  const handleAddItem = (name: string, category: string, quantity?: string, note?: string) => {
    onAddCustomItem?.(name, category, quantity, note);
    setShowAddItemSheet(false);
  };

  // Show progress when loading
  if (isLoadingShoppingList) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-8 text-center">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">
              {processingStage || 'Generating shopping list...'}
            </h3>
            <div className="w-full max-w-md mx-auto">
              <Progress value={progress} className="h-2" />
            </div>
            <p className="text-sm text-muted-foreground">
              {progress}% complete
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Please be patient while we generate your shopping list. This process typically takes 20-30 seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!shoppingList) return null;

  if (shoppingMode) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden relative">
        <div className="p-4 border-b flex items-center justify-between">
          <button
            onClick={onShoppingModeToggle}
            className="text-sm text-primary hover:underline"
          >
            ← Exit Shopping Mode
          </button>
          <div className="flex gap-2">
            {inactiveItems.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReactivateSheet(true)}
                className="text-muted-foreground"
              >
                <Clock className="h-4 w-4 mr-1" />
                Previous
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddItemSheet(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
        <div className="p-4 pb-6">
          <ShoppingListByCategory 
            categories={shoppingList.categories}
            customItems={customItems}
            onItemCheckedChange={onItemCheckedChange}
            onCustomItemCheckedChange={onCustomItemCheckedChange}
            onRemoveCustomItem={onRemoveCustomItem}
            shoppingMode={shoppingMode}
          />
        </div>
        
        {/* Add Item Sheet */}
        <Sheet open={showAddItemSheet} onOpenChange={setShowAddItemSheet}>
          <SheetContent side="bottom" className="rounded-t-2xl">
            <SheetHeader className="pb-4">
              <SheetTitle>Add Item</SheetTitle>
            </SheetHeader>
            <AddCustomItemForm
              existingCategories={getExistingCategories()}
              onAddItem={handleAddItem}
              onCancel={() => setShowAddItemSheet(false)}
            />
          </SheetContent>
        </Sheet>
        
        {/* Reactivate Items Sheet */}
        <ReactivateItemsSheet
          open={showReactivateSheet}
          onOpenChange={setShowReactivateSheet}
          inactiveItems={inactiveItems}
          onReactivate={(itemId) => {
            onReactivateItem?.(itemId);
          }}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden relative">      
      <ShoppingListHeader
        preferredUnits={(shoppingList?.generationParams?.unitSystem as UnitSystem) || preferredUnits}
        adults={shoppingList?.generationParams?.adults || adults}
        kids={shoppingList?.generationParams?.kids || kids}
        selectedDaysCount={shoppingList?.generationParams?.selectedDates?.length || selectedDates?.length || 0}
        mealTypeCounts={getMealTypeCounts()}
        recipeCount={getActualRecipeCount()}
        metadata={shoppingList.metadata}
        onShoppingModeToggle={onShoppingModeToggle}
        onEditSettings={onEditSettings}
      />

      <Tabs defaultValue="categories" className="w-full">
        <div className="px-4 py-2 border-b bg-gray-50">
          <TabsList className="grid grid-cols-2 w-full h-auto">
            <TabsTrigger value="categories" className="text-xs md:text-sm py-2">
              Shopping List ({getTotalIngredientCount()})
            </TabsTrigger>
            <TabsTrigger value="recipes" className="text-xs md:text-sm py-2">
              Recipes ({getActualRecipeCount()})
            </TabsTrigger>
          </TabsList>
        </div>
        
        <TabsContent value="categories" className="p-4 pb-6">
          {/* Add item button - at top */}
          <div className="mb-4 flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowAddItemSheet(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
            {inactiveItems.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => setShowReactivateSheet(true)}
                className="text-muted-foreground"
              >
                <Clock className="h-4 w-4 mr-1" />
                Previous ({inactiveItems.length})
              </Button>
            )}
          </div>
          
          <ShoppingListByCategory 
            categories={shoppingList.categories}
            customItems={customItems}
            onItemCheckedChange={onItemCheckedChange}
            onCustomItemCheckedChange={onCustomItemCheckedChange}
            onRemoveCustomItem={onRemoveCustomItem}
          />
        </TabsContent>
        
        <TabsContent value="recipes" className="p-4 pb-6">
          <RecipeIngredientsTab 
            recipes={shoppingList.recipes}
            scalingFactors={shoppingList.scalingFactors}
            compositions={shoppingList.compositions}
          />
        </TabsContent>
      </Tabs>
      
      {/* Add Item Sheet */}
      <Sheet open={showAddItemSheet} onOpenChange={setShowAddItemSheet}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="pb-4">
            <SheetTitle>Add Item</SheetTitle>
          </SheetHeader>
          <AddCustomItemForm
            existingCategories={getExistingCategories()}
            onAddItem={handleAddItem}
            onCancel={() => setShowAddItemSheet(false)}
          />
        </SheetContent>
      </Sheet>
      
      {/* Reactivate Items Sheet */}
      <ReactivateItemsSheet
        open={showReactivateSheet}
        onOpenChange={setShowReactivateSheet}
        inactiveItems={inactiveItems}
        onReactivate={(itemId) => {
          onReactivateItem?.(itemId);
        }}
      />
    </div>
  );
};

export default ShoppingListDisplay;
